import { Order, QueryEntity } from '@datorama/akita';
import { Injectable } from '@angular/core';
import { mergeTags, TagsStore, TagState } from './tags.store';
import { NewTagsService } from './tags.service';
import { Tag, TagResponse } from './tags.model';
import {
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    takeWhile,
    tap
} from 'rxjs/operators';
import { Instrument } from '../../../shared/state/instruments/instrument.model';
import { Acct } from '../../../shared/state/accts/acct.model';
import {
    checkFilter,
    flattenNestedObject,
    InferFilter
} from '../../../shared/state/query-filter.functions';
import { get } from 'dot-prop';
import { Cacheable } from 'ts-cacheable';

@Injectable({ providedIn: 'root' })
export class TagsQuery extends QueryEntity<TagState> {
    all$ = this.selectAll();
    allAsObject$ = this.selectAll({ asObject: true });
    selectById$ = (id: string) => this.selectEntity(id);

    hasLoaded$ = this.select((state) => state.loading).pipe(
        filter((loading) => !loading),
        takeWhile((hasLoaded) => !hasLoaded),
        shareReplay(1)
    );

    get all() {
        return this.getAll();
    }

    @Cacheable()
    selectTagsByFilter$(
        filters: InferFilter<TagResponse> = {},
        sortBy: keyof TagResponse = 'updatedAt',
        sortByOrder: Order = Order.DESC
    ) {
        return this.selectAll({
            sortBy,
            sortByOrder,
            filterBy: (tag) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(tag, key);
                        return !checkFilter(value, criteria);
                    });
                } else {
                    return true;
                }
            }
        }).pipe(
            map((tags) => mergeTags(tags)),
            shareReplay(1)
        );
    }

    selectByTitle$ = (title: string, group?: string) =>
        this.selectTagsByFilter$().pipe(
            map((tags) =>
                tags.find(
                    (tag) => tag.title === title && (group ? tag.group === group : true)
                )
            ),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            shareReplay(1)
        );

    getAllByTitle() {
        return mergeTags(this.getAll());
    }

    getById(id: string) {
        const title = this.getEntity(id)?.value;
        return this.getAllByTitle().find((tag) => tag.title === title);
    }

    getByTitle(title: string) {
        return this.getAllByTitle().find((tag) => tag.title === title);
    }

    asAccountMap$ = this.selectTagsByFilter$().pipe(
        map((tags) =>
            tags.reduce((acc, tag) => {
                Object.keys(tag.account || {}).forEach((accountId) => {
                    acc[accountId] = [...(acc[accountId] || []), tag];
                });
                return acc;
            }, {} as Record<Acct['accountId'], Array<Tag>>)
        ),
        shareReplay(1)
    );

    asInstrumentMap$ = this.selectTagsByFilter$().pipe(
        map((tags) =>
            tags.reduce((acc, tag) => {
                Object.keys(tag.instrument || {})?.forEach((instrumentId) => {
                    acc[instrumentId] = [...(acc[instrumentId] || []), tag];
                });
                return acc;
            }, {} as Record<Instrument['instrumentId'], Array<Tag>>)
        ),
        shareReplay(1)
    );

    @Cacheable()
    asGroupMap$(filters: InferFilter<TagResponse> = {}) {
        return this.selectTagsByFilter$(filters).pipe(
            map((tags) =>
                tags.reduce((acc, tag) => {
                    if (!!tag.group) {
                        acc[tag.group] = { ...(acc[tag.group] || {}), [tag.title]: tag };
                    }
                    return acc;
                }, {} as Record<string, Record<string, Tag>>)
            ),
            shareReplay(1)
        );
    }

    @Cacheable()
    selectByGroupAndItemId$(
        itemId?: string | number,
        filters: InferFilter<TagResponse> = {}
    ) {
        return this.selectTagsByFilter$(filters).pipe(
            map((tags) => {
                const groups: Record<
                    Tag['title'],
                    Record<
                        Partial<TagResponse['itemType'] | string>,
                        Record<typeof itemId, TagResponse>
                    >
                > = {};
                tags.forEach((tag) => {
                    const group = tag.group;
                    if (group) {
                        groups[group] = {
                            ...(groups[group] || {}),
                            instrument: {
                                ...(groups[group]?.instrument || {}),
                                ...tag.instrument
                            },
                            account: {
                                ...(groups[group]?.account || {}),
                                ...tag.account
                            }
                        };
                    }
                });
                return groups;
            }),
            shareReplay(1)
        );
        /*const temp = this.getAllByTitle().reduce((acc, tag) => {
            if (!!tag.group) {
                const instrumentIds = tag.instrumentIds;
                const instrumentTagMap = instrumentIds.reduce((acc, instrumentId) => {
                    acc[instrumentId] = tag;
                    return acc;
                }, {} as Record<Instrument['instrumentId'], Tag>);
                acc[tag.group] = { ...(acc[tag.group] || {}), ...instrumentTagMap };
            }
            return acc;
        }, {} as Record<string, Record<Instrument['instrumentId'], Tag>>);
        return temp[group]?.[instrumentId];*/
    }

    getByItemId(itemId: string, itemType?: string, group?: string) {
        return this.getAll({
            filterBy: (tag) =>
                tag.itemId === itemId &&
                (itemType ? tag.itemType === itemType : true) &&
                (group ? tag.group === group : true)
        });
    }

    getByGroupAndInstrumentId(group: string, instrumentId: string) {
        const temp = this.getAllByTitle().reduce((acc, tag) => {
            if (!!tag.group) {
                const instrumentIds = tag.instrument;
                const instrumentTagMap = Object.keys(instrumentIds).reduce(
                    (acc, instrumentId) => {
                        acc[instrumentId] = tag;
                        return acc;
                    },
                    {} as Record<Instrument['instrumentId'], Tag>
                );
                acc[tag.group] = { ...(acc[tag.group] || {}), ...instrumentTagMap };
            }
            return acc;
        }, {} as Record<string, Record<Instrument['instrumentId'], Tag>>);
        return temp[group]?.[instrumentId];
    }

    constructor(protected store: TagsStore, private _tagsService: NewTagsService) {
        super(store);
        this._tagsService.get().subscribe();
    }
}
